import React from 'react';

import QuizCTA from 'views/site/QuizCTA.js';
import Section, { InfoCard, SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * About
 *
 * A real page at ?route=about. "About" previously pointed at a waypoint on the
 * home page whose anchor stopped being rendered when the legacy AboutUs panel
 * was replaced, so it navigated home and did nothing.
 *
 * The dealership's own bio leads, because that is the part that is actually
 * about them; the mission copy below it is the shared text every replicated
 * website has carried for years and is used verbatim as the fallback when a
 * dealership has not written its own.
 *
 * The affiliation statement at the foot is not decoration. Some dealerships are
 * required to display it, and the wording is the approved wording — do not
 * paraphrase it.
 */

const MISSION = `Our mission is to educate families about the devastating effects of home fires and other home safety issues. Our seminars and one-on-one counseling teach families how to escape and survive the ravages of home fires, carbon monoxide poisoning and in some cases, even prevent property damage. Today's homes present numerous safety challenges. Unfortunately, most home owners are unaware of the hidden dangers they face. We strive to treat each family's safety as our own. Education and advanced detection equipment are the solutions when seconds count.`;

const VALUES = `We're committed to our mission of providing unlimited opportunity for growth to individuals, as we all work together to make the home YOU live in a more fire safe place. We are guided by respect for the individual, a passion for excellence, and an obsession with customer service. Our most valuable treasure is the people who represent our company and we treat them that way.`;

const About = ({ dealership, onOpenBlindSpots, onOpenQuiz, utils }) => {

    const mobile = Utils.isMobile();

    if(!dealership) {
        return null;
    }

    const location = dealership.locality && dealership.administrative_area_level_1
        ? `${dealership.locality}, ${dealership.administrative_area_level_1}`
        : null;

    const principles = [{
        copy: 'Every installation starts with an education. Families learn what to do before, during and after an emergency — where to meet, which routes are clear, how a closed door buys time — and then practise it.',
        icon: 'mortarboard-fill',
        title: 'We teach first, then we equip'
    },{
        copy: 'We walk the home with you and place each device where the manufacturer and NFPA 72 say it belongs. A kitchen and an attic are not the same problem, and neither is a hallway.',
        icon: 'house-door-fill',
        title: 'Every home is surveyed, not guessed'
    },{
        copy: 'No subscription and no monitoring contract, so nothing is owed each month to keep the alerts working. Pay for the network up front or over time, and it is yours once it is paid for.',
        icon: 'cash-coin',
        title: 'You own what we install'
    },{
        copy: 'We are a local company. The person who surveys your home is the person you call afterwards, and we are answerable to the families we sell to.',
        icon: 'people-fill',
        title: 'Locally owned and accountable'
    }];

    return (
        <div
        className={'site'}
        style={{
            paddingTop: Theme.layout.headerOffset(),
            width: '100%'
        }}>
            <section style={{
                backgroundColor: Theme.colors.offWhite,
                borderBottom: `1px solid ${Theme.colors.border()}`,
                width: '100%'
            }}>
                <div style={{
                    margin: '0 auto',
                    maxWidth: Theme.layout.maxWidth,
                    padding: mobile ? '40px 20px' : '64px 40px',
                    textAlign: 'center'
                }}>
                    <span style={{
                        ...Theme.textStyles.label(),
                        display: 'block',
                        marginBottom: 14
                    }}>{'About us'}</span>
                    <span style={{
                        ...Theme.textStyles.sectionHeading(),
                        display: 'block'
                    }}>{`About ${dealership.name}`}</span>
                    {location && (
                        <span style={{
                            ...Theme.textStyles.sectionSubheading(),
                            display: 'block',
                            margin: '16px auto 0'
                        }}>{VALUES}</span>
                    )}
                </div>
            </section>

            <Section tinted={false}>
                <SectionHeader
                eyebrow={'How we work'}
                subtitle={'The same four things, in every home we walk into. We are a local dealership rather than a call centre, so the person who walks through your home is the person who answers when you ring afterwards. None of it changes with the size of the order.'}
                title={'What you can expect from us'} />

                <div style={{
                    display: 'grid',
                    gap: mobile ? 16 : 20,
                    gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)'
                }}>
                    {principles.map((principle, index) => (
                        <InfoCard key={index} {...principle} />
                    ))}
                </div>
            </Section>

            {/* The dealership's own words, or the shared mission as fallback */}
            <Section tinted={true}>
                <div style={{
                    alignItems: 'center',
                    display: 'grid',
                    gap: mobile ? 32 : 64,
                    gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
                }}>
                    <div>
                        <span style={{
                            ...Theme.textStyles.heading({ fontSize: mobile ? 24 : 32 }),
                            display: 'block',
                            marginBottom: 16
                        }}>{'Who we are'}</span>
                        <span style={{
                            ...Theme.textStyles.body({ fontSize: mobile ? 15 : 16, lineHeight: 1.7 }),
                            display: 'block'
                        }}>{dealership.bio || MISSION}</span>
                    </div>
                    <div style={{
                        borderRadius: 20,
                        overflow: 'hidden'
                    }}>
                        <img
                        alt={`The ${dealership.name} team`}
                        src={'images/family-sales.jpg'}
                        style={{
                            display: 'block',
                            height: mobile ? 240 : 380,
                            objectFit: 'cover',
                            objectPosition: '74% 42%',
                            width: '100%'
                        }} />
                    </div>
                </div>
            </Section>

            <QuizCTA
            dealership={dealership}
            heading={'Ready to see what your home needs?'}
            onOpenQuiz={onOpenBlindSpots || onOpenQuiz}
            subtext={'It takes about a minute and ends with a list you can hand straight to us or change first.'}
            /* Last thing before the footer, which is dark. Two dark bands
               meeting produce a seam with no edge and the call to action stops
               reading as its own thing. */
            tone={'light'} />

        </div>
    )
}

export default About;
